import React, { useState, useEffect, useMemo } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  Wallet,
  Send,
  Users,
  ArrowLeftRight,
  BarChart3,
  LogOut,
  Menu,
  X,
  Globe2,
  Settings as SettingsIcon,
  User as UserIcon,
  CreditCard,
  ListChecks,
  Crown,
  Smartphone,
  ShieldCheck,
  TrendingUp,
  ScrollText,
  Activity,
  Network,
  KeyRound,
  Percent,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
  ClipboardList,
  FileText,
  Landmark,
  Palette,
  Layers,
  Coins,
  UserCog,
  CheckSquare,
  History,
  Gift,
  Zap,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { cn, formatUSD } from "@/lib/utils";
import api from "@/lib/api";

const ALL = ["MASTER_ADMIN", "MASTER_AGENT", "AGENT"];
const ADMIN_AND_MASTER = ["MASTER_ADMIN", "MASTER_AGENT"];
// Roles that reach the Master Admin's global surface: admin himself + his
// trusted collaborators. Individual permission keys further narrow what
// each COLLABORATOR sees inside these screens.
const ADMIN_OR_COLLAB = ["MASTER_ADMIN", "COLLABORATOR"];
const ADMIN_MA_COLLAB = ["MASTER_ADMIN", "MASTER_AGENT", "COLLABORATOR"];

/**
 * Navigation model.
 *
 * Two node shapes:
 *   { kind: 'item',   to, label, icon, roles, testid }
 *   { kind: 'folder', id, label, icon, roles, children: [item, …] }
 *
 * Folders remember their open state in localStorage. On navigation, the
 * folder that contains the active route is auto-expanded.
 */
/**
 * Sidebar principles (per user, Feb 2026):
 *   1. Never duplicate the same feature in different folders.
 *   2. "My Agents" folder contains ONLY agent-tier features.
 *   3. "Master Agents" folder contains ONLY master-agent-tier features.
 *   4. Pages that already have their own "Create" button do NOT get a
 *      separate "Create …" menu link (e.g. Agent Management, Master Agents List).
 *   5. Cross-tier features (Pending Codes, My Commissions) live at top level.
 *   6. Sidebar stays short and professional — banking-app aesthetic.
 */
const NAV = [
  { kind: "item", to: "/profile", label: "nav.profile", icon: UserIcon, roles: ["MASTER_ADMIN", "MASTER_AGENT", "AGENT", "CASH_VERIFIER", "COLLABORATOR"], testid: "nav-profile" },
  { kind: "item", to: "/dashboard", label: "nav.dashboard", icon: LayoutDashboard, roles: [...ALL, "COLLABORATOR"], testid: "nav-dashboard" },
  {
    kind: "folder",
    id: "my_wallet",
    label: "nav.folder_my_wallet",
    icon: Wallet,
    iconOpen: Wallet,
    roles: [...ALL, "COLLABORATOR"],
    permission: "view_balances",
    testid: "nav-folder-my-wallet",
    children: [
      { to: "/wallet/currencies", label: "nav.folder_currencies", icon: Coins, testid: "nav-wallet-currencies", permission: "view_balances" },
      { to: "/wallet/external-transfer", label: "nav.folder_send_external_transfer", icon: ArrowLeftRight, testid: "nav-wallet-external-transfer", permission: "view_balances" },
      { to: "/wallet/funding-history", label: "nav.folder_funding_history", icon: History, testid: "nav-wallet-funding-history", permission: "view_balances" },
    ],
  },
  { kind: "item", to: "/send-topup", label: "nav.send_topup", icon: Send, roles: [...ALL, "COLLABORATOR"], anyPermission: ["view_topups", "create_topups"], testid: "nav-send-topup" },
  { kind: "item", to: "/gift-cards", label: "nav.gift_cards", icon: Gift, roles: [...ALL, "COLLABORATOR"], anyPermission: ["view_topups", "create_topups"], testid: "nav-gift-cards" },
  { kind: "item", to: "/pay-bills", label: "nav.pay_bills", icon: Zap, roles: [...ALL, "COLLABORATOR"], anyPermission: ["view_topups", "create_topups"], testid: "nav-pay-bills" },
  { kind: "item", to: "/send-cash", label: "nav.send_cash", icon: Smartphone, roles: [...ALL, "COLLABORATOR"], anyPermission: ["view_sendcash", "create_sendcash"], testid: "nav-send-cash" },
  { kind: "item", to: "/make-payment", label: "nav.make_payment", icon: CreditCard, roles: [...ALL, "COLLABORATOR"], anyPermission: ["view_payku", "view_payment_history"], testid: "nav-make-payment" },
  { kind: "item", to: "/transfer-funds", label: "nav.transfer_funds", icon: ArrowLeftRight, roles: [...ADMIN_AND_MASTER, "COLLABORATOR"], permission: "transfer_funds", testid: "nav-transfer-funds" },

  // ── My Agents (MASTER_AGENT + MASTER_ADMIN + COLLABORATOR w/ view_agents)
  {
    kind: "folder",
    id: "my_agents",
    label: "nav.folder_my_agents",
    icon: Folder,
    iconOpen: FolderOpen,
    roles: [...ADMIN_AND_MASTER, "COLLABORATOR"],
    permission: "view_agents",
    testid: "nav-folder-my-agents",
    children: [
      { to: "/agents", label: "nav.folder_agent_management", icon: Users, testid: "nav-agent-management", permission: "view_agents" },
      { to: "/subagent-transactions", label: "nav.folder_subagent_tx", icon: ListChecks, testid: "nav-subagent-tx", permission: "view_agent_transactions" },
      { to: "/reports?tab=agents", match: "/reports", label: "nav.folder_subagent_sales", icon: TrendingUp, testid: "nav-subagent-sales", permission: "view_agent_sales" },
      { to: "/set-commissions?role=AGENT", match: "/set-commissions", label: "nav.folder_set_agent_commissions", icon: Percent, testid: "nav-set-agent-commissions", permission: "edit_agent_commissions" },
      { to: "/master-agent-commissions", label: "nav.master_commissions", icon: Coins, testid: "nav-my-master-commissions", roles: ["MASTER_AGENT"] },
      { to: "/analytics?scope=agents", match: "/analytics", label: "nav.folder_network_performance", icon: Activity, testid: "nav-network-performance", permission: "view_reports" },
    ],
  },

  // ── Master Agents (MASTER_ADMIN + COLLABORATOR w/ view_master_agents) ──
  {
    kind: "folder",
    id: "master_agents",
    label: "nav.folder_master_agents",
    icon: Folder,
    iconOpen: FolderOpen,
    roles: ADMIN_OR_COLLAB,
    permission: "view_master_agents",
    testid: "nav-folder-master-agents",
    children: [
      { to: "/master-agents", label: "nav.folder_master_list", icon: Crown, testid: "nav-master-list", permission: "view_master_agents" },
      { to: "/master-commissions-payouts", label: "nav.folder_commission_reports", icon: FileText, testid: "nav-master-commission-reports", permission: "view_commissions" },
    ],
  },

  // ── Collaborators (MASTER_ADMIN only) ─────────────────────────────────
  {
    kind: "folder",
    id: "collaborators",
    label: "nav.folder_collaborators",
    icon: Folder,
    iconOpen: FolderOpen,
    roles: ["MASTER_ADMIN"],
    testid: "nav-folder-collaborators",
    children: [
      { to: "/collaborators", label: "nav.folder_collaborator_management", icon: UserCog, testid: "nav-collab-management" },
      { to: "/collaborators/activity", label: "nav.folder_collaborator_activity", icon: ClipboardList, testid: "nav-collab-activity" },
      { to: "/collaborators/sessions", label: "nav.folder_collaborator_sessions", icon: ShieldCheck, testid: "nav-collab-sessions" },
      { to: "/approvals", label: "nav.folder_collaborator_approvals", icon: CheckSquare, testid: "nav-collab-approvals" },
      { to: "/compliance", label: "nav.folder_compliance", icon: Activity, testid: "nav-compliance" },
    ],
  },

  // Cross-tier & shared items (top level)
  { kind: "item", to: "/pending-codes", label: "nav.pending_codes", icon: KeyRound, roles: [...ADMIN_AND_MASTER, "COLLABORATOR"], permission: "view_pending_codes", testid: "nav-pending-codes" },
  { kind: "item", to: "/pending-password-reset-codes", label: "nav.pending_password_reset_codes", icon: KeyRound, roles: [...ADMIN_AND_MASTER, "COLLABORATOR"], permission: "view_pending_password_reset_codes", testid: "nav-pending-password-reset-codes" },
  { kind: "item", to: "/my-commissions", label: "nav.my_commissions", icon: Percent, roles: ["AGENT"], testid: "nav-my-commissions" },

  { kind: "item", to: "/reports", label: "nav.reports", icon: BarChart3, roles: [...ALL, "COLLABORATOR"], permission: "view_reports", testid: "nav-reports" },
  { kind: "item", to: "/analytics", label: "nav.analytics", icon: Activity, roles: ["AGENT", "CASH_VERIFIER"], testid: "nav-analytics" },
  { kind: "item", to: "/cash-verification", label: "nav.cash_verification", icon: ShieldCheck, roles: ["MASTER_ADMIN", "CASH_VERIFIER", "COLLABORATOR"], permission: "view_cash_verification", testid: "nav-cash-verification" },
  { kind: "item", to: "/exchange-rates", label: "nav.exchange_rates", icon: Landmark, roles: ADMIN_OR_COLLAB, permission: "view_exchange_rates", testid: "nav-exchange-rates" },
  { kind: "item", to: "/landing-settings", label: "nav.branding_settings", icon: Palette, roles: ["MASTER_ADMIN"], testid: "nav-branding-settings" },
  { kind: "item", to: "/trusted-devices", label: "nav.trusted_devices", icon: ShieldCheck, roles: ["MASTER_ADMIN", "COLLABORATOR"], permission: "view_trusted_devices", testid: "nav-trusted-devices" },
  { kind: "item", to: "/sms-diagnostics", label: "nav.sms_diagnostics", icon: Activity, roles: ["MASTER_ADMIN"], testid: "nav-sms-diagnostics" },
  { kind: "item", to: "/provider", label: "nav.provider_management", icon: Network, roles: ["MASTER_ADMIN"], testid: "nav-provider-management" },
  { kind: "item", to: "/audit-log", label: "nav.audit_log", icon: ScrollText, roles: ["MASTER_ADMIN"], testid: "nav-audit-log" },
  { kind: "item", to: "/audit-log", label: "nav.my_activity", icon: ClipboardList, roles: ["MASTER_AGENT", "AGENT", "CASH_VERIFIER", "COLLABORATOR"], testid: "nav-my-activity" },
  { kind: "item", to: "/settings", label: "nav.settings", icon: SettingsIcon, roles: [...ALL, "COLLABORATOR"], testid: "nav-settings" },
];

const ROLE_BADGE_TONE = {
  MASTER_ADMIN: "bg-amber-50 text-amber-700 border-amber-200",
  MASTER_AGENT: "bg-violet-50 text-violet-700 border-violet-200",
  AGENT: "bg-slate-100 text-slate-700 border-slate-200",
  CASH_VERIFIER: "bg-sky-50 text-sky-700 border-sky-200",
  COLLABORATOR: "bg-teal-50 text-teal-700 border-teal-200",
};

const FOLDERS_STORAGE_KEY = "globetopup.sidebar.folders";

function loadFolderState() {
  try {
    return JSON.parse(localStorage.getItem(FOLDERS_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}
function saveFolderState(state) {
  try {
    localStorage.setItem(FOLDERS_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* localStorage may be blocked (private mode) */
  }
}

function pathMatchesChild(pathname, child) {
  const target = child.match || child.to.split("?")[0];
  if (target === pathname) return true;
  // treat "/agents" as matching "/agents" (exact) — we don't do prefix
  // matching so /agents doesn't shadow deeper agent routes.
  return false;
}

function SidebarContent({ user, onNavigate }) {
  const { t } = useTranslation();
  const { brand } = useBranding();
  const location = useLocation();

  // Determine which folder contains the active route so we can auto-expand it.
  const activeFolderId = useMemo(() => {
    for (const node of NAV) {
      if (node.kind !== "folder") continue;
      if (node.children.some((c) => pathMatchesChild(location.pathname, c))) {
        return node.id;
      }
    }
    return null;
  }, [location.pathname]);

  const [openFolders, setOpenFolders] = useState(() => {
    const persisted = loadFolderState();
    // Auto-open the active folder on first mount if it isn't remembered as closed.
    return persisted;
  });

  useEffect(() => {
    if (activeFolderId && openFolders[activeFolderId] === undefined) {
      const next = { ...openFolders, [activeFolderId]: true };
      setOpenFolders(next);
      saveFolderState(next);
    }
  }, [activeFolderId, openFolders]);

  const toggleFolder = (id) => {
    const next = { ...openFolders, [id]: !openFolders[id] };
    setOpenFolders(next);
    saveFolderState(next);
  };

  // Filter nav by role AND — for COLLABORATOR — by granular permissions
  // declared via optional `permission` (single key) or `anyPermission` (array).
  const hasPerm = (node) => {
    if (user.role !== "COLLABORATOR") return true;
    const perms = user.permissions || {};
    const allowed = (k) => {
      const m = perms[k];
      return m === "allowed" || m === "approval";
    };
    if (node.permission && !allowed(node.permission)) return false;
    if (node.anyPermission && !node.anyPermission.some(allowed)) return false;
    return true;
  };
  const visible = NAV.filter((n) => n.roles.includes(user.role) && hasPerm(n));

  return (
    <>
      <div className="px-5 py-5 border-b border-slate-200 flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-[#0F62FE] text-white flex items-center justify-center overflow-hidden">
          {brand.brand_logo_url ? (
            <img src={brand.brand_logo_url} alt="logo" className="w-full h-full object-contain" />
          ) : (
            <Globe2 className="w-5 h-5" strokeWidth={2.2} />
          )}
        </div>
        <div>
          <div className="font-bold text-slate-900 leading-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {brand.brand_name}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{brand.brand_tagline}</div>
        </div>
      </div>

      <nav className="flex-1 py-3 overflow-y-auto" data-testid="sidebar-nav">
        <div className="px-4 pb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
          {t("nav.section_navigation", "Navigation")}
        </div>
        {visible.map((node) => {
          if (node.kind === "item") {
            return (
              <NavItem key={`${node.to}:${node.label}`} node={node} onNavigate={onNavigate} t={t} />
            );
          }
          // folder
          const isOpen = !!openFolders[node.id];
          const FolderIcon = isOpen && node.iconOpen ? node.iconOpen : node.icon;
          // Per-child role filter: children may declare their own `roles`
          // array to restrict visibility beyond the folder's `roles`.
          const visibleChildren = node.children.filter(
            (c) => (!c.roles || c.roles.includes(user.role)) && hasPerm(c)
          );
          if (visibleChildren.length === 0) return null;
          return (
            <div key={node.id} className="mb-0.5">
              <button
                type="button"
                data-testid={node.testid}
                data-state={isOpen ? "open" : "closed"}
                onClick={() => toggleFolder(node.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mx-2",
                  "hover:bg-slate-100 text-slate-700"
                )}
              >
                <FolderIcon className="w-4 h-4" strokeWidth={2} />
                <span className="flex-1 text-left">{t(node.label)}</span>
                {isOpen ? (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                )}
              </button>
              {isOpen && (
                <div
                  data-testid={`${node.testid}-panel`}
                  className="mx-2 mb-1 border-l border-slate-200 ml-6 pl-2 animate-in fade-in slide-in-from-top-1 duration-150"
                >
                  {visibleChildren.map((c) => (
                    <NavLink
                      key={c.testid}
                      to={c.to}
                      end
                      data-testid={c.testid}
                      onClick={onNavigate}
                      className={({ isActive }) => {
                        // `end` above forces NavLink to require an EXACT path match,
                        // so `/collaborators` no longer stays highlighted when the
                        // user is on `/collaborators/activity`. We still OR with our
                        // helper so children can declare custom `match` overrides.
                        const isMatch = isActive || pathMatchesChild(location.pathname, c);
                        return cn(
                          "flex items-center gap-2 pl-3 pr-2 py-2 rounded-md text-[13px] transition-colors my-0.5",
                          isMatch
                            ? "bg-[#EDF5FF] text-[#0F62FE] font-semibold"
                            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                        );
                      }}
                    >
                      {c.icon && <c.icon className="w-3.5 h-3.5" strokeWidth={2} />}
                      <span className="truncate">{t(c.label)}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="px-4 py-4 border-t border-slate-200">
        <div className="text-xs text-slate-500">{t("nav.signed_in_as")}</div>
        <div className="text-sm font-semibold text-slate-900 truncate" data-testid="sidebar-user-name">{user.name}</div>
        <div className="text-xs text-slate-500 mt-0.5">
          <span
            data-testid="sidebar-role-badge"
            className={cn(
              "inline-flex px-2 py-0.5 rounded-full font-semibold text-[10px] uppercase tracking-wider border",
              ROLE_BADGE_TONE[user.role] || ROLE_BADGE_TONE.AGENT,
            )}
          >
            {t(`role.${user.role}`, user.role)}
          </span>
        </div>
      </div>
    </>
  );
}

function NavItem({ node, onNavigate, t }) {
  return (
    <NavLink
      to={node.to}
      data-testid={node.testid}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mx-2 my-0.5",
          isActive
            ? "bg-[#EDF5FF] text-[#0F62FE]"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        )
      }
    >
      <node.icon className="w-4 h-4" strokeWidth={2} />
      <span>{t(node.label)}</span>
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { brand } = useBranding();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true
  );
  const [walletData, setWalletData] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    let mounted = true;
    api.get("/wallet").then((r) => mounted && setWalletData(r.data)).catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  // Header balance pill — role-aware:
  //   MASTER_ADMIN     → external provider balance (DingConnect) via balance_usd
  //   COLLABORATOR     → delegated Master Wallet USD (internal_allocation_usd)
  //   MASTER_AGENT/AGENT → their own USD wallet (balance_usd)
  // The external provider balance is NEVER shown to non-MASTER_ADMIN roles.
  const headerBalance = !walletData
    ? null
    : user?.role === "MASTER_ADMIN"
      ? walletData.balance_usd
      : user?.role === "COLLABORATOR"
        ? (walletData.internal_allocation_usd ?? walletData.balance_usd)
        : walletData.balance_usd;
  const headerBalanceLabel = user?.role === "MASTER_ADMIN"
    ? t("layout.pill_provider_balance", "Provider balance (external)")
    : t("layout.pill_master_wallet_usd", "Master Wallet · USD");

  if (!user) return null;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Desktop sidebar — only mounted at lg+ so its data-testids don't collide with the mobile drawer. */}
      {isDesktop && (
        <aside className="flex w-64 bg-white border-r border-slate-200 flex-col h-screen fixed top-0 left-0 z-40">
          <SidebarContent user={user} />
        </aside>
      )}

      {/* Mobile drawer */}
      {!isDesktop && mobileOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white flex flex-col shadow-xl">
            <div className="flex justify-end p-2">
              <button
                data-testid="sidebar-close-btn"
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded-lg hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <SidebarContent user={user} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Header */}
      <header className="h-12 bg-[#2BA8F0] sticky top-0 z-30 flex items-center justify-between px-4 sm:px-6 lg:px-8 lg:ml-64">
        <div className="flex items-center gap-3">
          <button
            data-testid="sidebar-open-btn"
            className="lg:hidden p-1.5 rounded hover:bg-white/15 text-white"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/dashboard" className="lg:hidden font-bold text-white text-base" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {brand.brand_name}
          </Link>
          <span
            data-testid="header-user-name"
            className="hidden lg:inline text-white/95 text-sm font-semibold tracking-wide"
          >
            {user.name}
          </span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <div
            data-testid="header-balance-pill"
            title={headerBalanceLabel}
            className="hidden sm:flex items-center gap-1.5 px-3 h-8 rounded bg-white/15 text-white text-sm font-semibold border border-white/25"
          >
            <Wallet className="w-4 h-4" />
            {headerBalance == null ? "—" : formatUSD(headerBalance)}
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="inline-flex items-center gap-1.5 px-3 h-8 rounded bg-white/10 hover:bg-white/20 border border-white/25 text-sm text-white font-medium"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">{t("nav.logout")}</span>
          </button>
        </div>
      </header>

      <main className="lg:ml-64 min-h-[calc(100vh-3rem)] p-4 sm:p-6 lg:p-8 animate-in fade-in duration-300">
        {children}
      </main>
    </div>
  );
}
